import React, { useState } from 'react';
import {
  bundledDatesRangeOptions,
  BundledOrCustomDatesRangeOption,
  DateRangeFilterDropdown,
  DatesRangeOption,
} from '@good/ui/templates';
import { Info } from 'design-components';
import { FilterTypeDateRange, FilterDateRangeValues, IFilterDateRange } from 'interfaces/filter-interfaces';
import { timeZone } from 'interfaces/timezone-type';
import { Group } from 'react-aria-components';
import { FilterType } from 'utilities/enum-utils';
import { usePayPeriodDatesRanges } from './use-pay-period-dates-ranges';
import { DatesRangeValue } from '@good/ui/dates';
import { useTranslation } from 'react-i18next';
import { Menu, Tooltip } from '@good/ui/core';
import { label } from 'design-components/label/label.css';

export type SaveNewFilterValueDateRange = (
  filterType: FilterTypeDateRange,
  newFilterValue: FilterDateRangeValues,
  selectionLabel: string,
) => void;

interface IFilterMenuDateRangeProps {
  filter: IFilterDateRange;
  canRemove?: boolean;
  displayTimezone: timeZone;
  saveNewFilterValue: SaveNewFilterValueDateRange;
  removeFilter: (filterType: FilterType | string) => void;
  onClose?: () => void;
  value: { startDate: Date | null; endDate: Date | null };
  onChange: (value: { startDate: Date | null; endDate: Date | null }, label?: string) => void;
}

function isValidDatesRange(datesRange: DatesRangeValue): datesRange is [Date, Date] {
  return datesRange[0] instanceof Date && datesRange[1] instanceof Date;
}

export const DateRangeFilterV2 = ({ value: valueProp, onChange: onChangeProp }: IFilterMenuDateRangeProps) => {
  const { t } = useTranslation('', { keyPrefix: 'payments.timesheets.filters' });

  const [valueInternal, setValueInternal] = useState(valueProp);

  const hasValidStartAndEndDate = valueInternal.startDate !== null && valueInternal.endDate !== null;
  const valueToUse = valueProp ?? valueInternal;
  const value = hasValidStartAndEndDate ? valueToUse : valueInternal;

  const onChange = (newValue: { startDate: Date | null; endDate: Date | null }, label?: string) => {
    setValueInternal(newValue);
    if (newValue.startDate !== null && newValue.endDate !== null) {
      onChangeProp(newValue, label);
    }
  };

  const [hasPayPeriod, setHasPayPeriod] = useState(true);
  const defaultDateRange = bundledDatesRangeOptions['this-week'].range;
  const {
    currentPayPeriodRange,
    previousPayPeriodRange,
    isLoading: loadingPayPeriods,
  } = usePayPeriodDatesRanges((currentPayPeriodRange) => {
    if (!value.startDate || !value.endDate) {
      if (isValidDatesRange(currentPayPeriodRange)) {
        onChange({
          startDate: currentPayPeriodRange[0],
          endDate: currentPayPeriodRange[1],
        });
      } else {
        setHasPayPeriod(false);
        onChange({
          startDate: defaultDateRange[0],
          endDate: defaultDateRange[1],
        });
      }
    }
  });
  const payPeriodDatesRangeOptions: DatesRangeOption[] | undefined =
    isValidDatesRange(currentPayPeriodRange) && isValidDatesRange(previousPayPeriodRange)
      ? [
          {
            label: t('date.options.current'),
            range: currentPayPeriodRange,
          },
          {
            label: t('date.options.previous'),
            range: previousPayPeriodRange,
          },
        ]
      : undefined;
  const customDatesRangeOptions: BundledOrCustomDatesRangeOption[] = [
    'this-week',
    'last-week',
    'this-fortnight',
    'last-fortnight',
    'this-year',
    'last-year',
  ];

  const { startDate, endDate } = value;

  return (
    <DateRangeFilterDropdown
      value={[startDate, endDate]}
      onChange={([startDate, endDate], label) =>
        onChange(
          {
            startDate,
            endDate,
          },
          label,
        )
      }
      initialValue={[startDate, endDate]}
      onClose={() => {}}
      label={t('date.label')}
      rangeOptions={
        payPeriodDatesRangeOptions
          ? [
              ...payPeriodDatesRangeOptions,
              {
                sectionLabel: t('date.options.custom'),
                ranges: customDatesRangeOptions,
              },
            ]
          : customDatesRangeOptions
      }
      rangeOptionsTitle={
        <Group gap={6}>
          {t('date.title')}
          {!hasPayPeriod && (
            <Tooltip label={t('date.noPayPeriodConfigured')} position='top-start' color='lavender.9' withArrow>
              <Info />
            </Tooltip>
          )}
        </Group>
      }
      loading={loadingPayPeriods}
    />
  );
};
